// Server-driven feature flag with two storage tiers:
//
//   1. localStorage["uploader_override"]  — explicit dev/QA override. If set, wins over
//      everything. No UI for it; set it manually in DevTools. Used to preview a mode
//      without flipping it for all users.
//   2. localStorage["uploader"]           — last-known server value. Acts as a cache so
//      cold-start renders the correct mode before the boot fetch resolves. Written by
//      applyServerUploader() on every successful fetch / admin update.
//
// Synchronous read path: getActiveUploader() is called from non-React contexts (the
// resume banner's boot effect, ChunkedUploadService, etc.) so it cannot await Redux.
// The cache exists specifically to give those callers a sensible value immediately.

const CACHE_KEY = "uploader";
const OVERRIDE_KEY = "uploader_override";

export const UPLOADER_LEGACY = "v1";
export const UPLOADER_CHUNKED = "v2";
const VALID = new Set([UPLOADER_LEGACY, UPLOADER_CHUNKED]);

// Conservative default — if neither the override nor the cache has a valid value, fall
// back to legacy until the server fetch lands. Safer than chunked-by-default for users
// who haven't completed first boot yet.
const DEFAULT = UPLOADER_LEGACY;

const listeners = new Set();

const readStorage = (key) => {
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem(key);
    return v && VALID.has(v) ? v : null;
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {
    // private mode / quota — non-fatal
  }
};

// Synchronous read used by non-React callers. Resolution: override → cache → default.
export const getActiveUploader = () => {
  const override = readStorage(OVERRIDE_KEY);
  if (override) return override;
  const cached = readStorage(CACHE_KEY);
  if (cached) return cached;
  return DEFAULT;
};

// Read the cache only (used to seed Redux initialState before the boot fetch).
export const readCachedUploader = () => readStorage(CACHE_KEY);

// Called by the systemSettings slice when the server returns a new value. Updates the
// cache and notifies subscribers (e.g. the admin page's radio control).
export const applyServerUploader = (value) => {
  if (!VALID.has(value)) return;
  writeStorage(CACHE_KEY, value);
  listeners.forEach((listener) => {
    try {
      listener(value);
    } catch {
      // listener errors are isolated — one broken subscriber must not break the rest
    }
  });
};

export const subscribeUploader = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const isChunkedUploaderActive = () => getActiveUploader() === UPLOADER_CHUNKED;
